import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { CarrierType, CarrierVisitDirection, OrderResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { VesselVisitItemStore } from '@planning/rt-stores/vesselVisit/VesselVisitItemStore'
import { orderService, vesselVisitService } from '@planning/services'
import companyService from '@planning/services/companyService'
import { useNavigationStore } from '@planning/stores'
import { PrintoutsViewStore } from '@planning/stores/printouts/PrintoutViewStore'
import { canOrderProceed } from '@planning/utils/order-utils'
import { getHoldEnforcementResult, masterHoldTypes } from '@planning/utils/hold-enforcement'
import { mapToEirData } from '@planning/utils/printout-utils'
import { useTranslate } from '@tolgee/react'
import {
  EquipmentInterchangeReceipt,
  Header,
  Loader,
  StepperInfo,
  useMinimalsTheme,
} from '@tom-ui/ui'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo } from 'react'
import { IInspectContainerFormData } from './Components/InspectContainer'
import { RoutingSlip, RoutingSlipItem } from './Components/RoutingSlip'
import { IGateInFormData } from './GateInForm'
import { IGateOutFormData } from './GateOutForm'
import { useSelectedGateLabel } from './hooks/useSelectedGateLabel'
import { GateClerkDocumentsPageViewStore } from './Stores/GateClerkDocumentsPageViewStore'

type GateClerkDocumentsPageProps = {
  direction: CarrierVisitDirection
  viewStore: GateClerkDocumentsPageViewStore
  gateInData?: IGateInFormData
  gateOutData?: IGateOutFormData
}

export async function generateEirDataForOrder(
  order: IInspectContainerFormData,
  now: Date,
  {
    truckPlate,
    truckCompanyName,
    driverName,
    driverID,
    fetchLinkedOrderData,
    orderItemStore,
    vesselVisitItemStore,
  }: {
    truckPlate: string
    truckCompanyName?: string
    driverName?: string
    driverID?: string
    fetchLinkedOrderData: (orderId: number) => Promise<OrderResponseDto | null>
    orderItemStore: OrderItemStore
    vesselVisitItemStore: VesselVisitItemStore
  },
) {
  const linkedOrder = await fetchLinkedOrderData(order.id)
  const fullOrder = Object.values(orderItemStore.elements).find(o => o.id === order.id)
  const finalOrderData = { ...fullOrder?.data, ...order }

  let voyageNumber = '-'
  if (linkedOrder?.carrierVisitId && linkedOrder.carrierVisitType === CarrierType.Vessel) {
    await vesselVisitService.getById(linkedOrder.carrierVisitId)
    const linkedVisit = vesselVisitItemStore.getById(linkedOrder.carrierVisitId)
    voyageNumber =
      linkedVisit?.voyages
        ?.map(v =>
          order.direction === CarrierVisitDirection.Inbound ? v.inboundNumber : v.outboundNumber,
        )
        .filter(Boolean)
        .join(' / ') || '-'
  }

  let company
  try {
    if (order.consignee) {
      company = await companyService.getByShortName(order.consignee)
    }
  } catch (error) {
    console.error(`Error fetching company for: ${order.consignee}`, error)
  }

  return mapToEirData({
    order: finalOrderData,
    linkedOrder: {
      carrierName: linkedOrder?.carrierName,
      shippingLine: linkedOrder?.shippingLine,
    },
    visit: {
      now,
      licensePlate: truckPlate,
      driverName,
      driverID,
      companyName: truckCompanyName,
      customerName: order.customerName ?? undefined,
    },
    voyageNumber,
    consigneeAddress: company?.address ?? '-',
    dangerousGoodsDefinition: finalOrderData?.dangerousGoodsDefinitions,
    isDamaged: finalOrderData?.isDamaged ?? finalOrderData.damages.length > 0,
  })
}

const GateClerkDocumentsPage: React.FC<GateClerkDocumentsPageProps> = observer(
  ({ direction, viewStore, gateInData, gateOutData }) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const navigationStore = useNavigationStore()
    const gateLabel = useSelectedGateLabel()

    const { gateClerkViewStore, orderItemStore, vesselVisitItemStore, tenantStore, holdItemStore } =
      usePlanningStore()

    const printoutsViewStore = useMemo(() => new PrintoutsViewStore(), [])

    const fetchLinkedOrderData = useCallback(
      async (orderId: number) => {
        const order = _.values(orderItemStore.elements).filter(o => o.id === orderId)[0]
        const linkedOrderId = order?.data?.linkedOrderId

        let linkedOrder: OrderResponseDto | null = null

        if (linkedOrderId) {
          linkedOrder = await orderService.getById(linkedOrderId)
        }

        return linkedOrder
      },
      [orderItemStore.elements],
    )

    const generateRoutingSlipPdf = useCallback(
      async (now: Date) => {
        const isGeneralCargoOrder = (order: IInspectContainerFormData) =>
          !order.containerNumber && order.commodityId !== null && order.commodityId !== undefined
        const generalCargoDropOffOrders = (
          gateInData?.dropOffOrders ?? gateClerkViewStore.gateInViewStore.dropOffOrders
        ).filter(isGeneralCargoOrder)

        const generalCargoPickUpOrders = (
          gateInData?.pickUpOrders ?? gateClerkViewStore.gateInViewStore.pickUpOrders
        ).filter(isGeneralCargoOrder)

        const generalCargoLocations = new Map<number, string>(
          viewStore.routingInfoData
            .filter(
              data => data.type === 'generalCargoDropOff' || data.type === 'generalCargoPickUp',
            )
            .filter(data => typeof data.generalCargoOrderId === 'number')
            .map(data => [data.generalCargoOrderId!, data.generalCargoLocation ?? '']),
        )

        const dropOffs: RoutingSlipItem[] = [
          ...viewStore.routingInfoData
            .filter(c => c.type === 'dropOff' && !!c.containerNumber)
            .map(c => ({
              type: 'numeric' as const,
              containerNumber: c.containerNumber!,
              location: c.blockName ?? 'No location provided',
            })),
          ...generalCargoDropOffOrders.map(order => ({
            type: 'generalCargo' as const,
            referenceNumber: order.referenceNumber ?? undefined,
            commodityName: order.commodityName ?? undefined,
            commodityAttributes: order.commodityAttributes ?? undefined,
            plannedCargoAmount: order.plannedCargoAmount ?? undefined,
            location:
              generalCargoLocations.get(order.id)?.trim() ||
              t('noLocationProvided', 'No location provided'),
          })),
        ]

        const pickUpOrders: RoutingSlipItem[] = [
          ...viewStore.routingInfoData
            .filter(c => c.type === 'pickUp' && !!c.containerNumber)
            .map(c => ({
              type: 'numeric' as const,
              containerNumber: c.containerNumber!,
              location: c.blockName ?? 'No location provided',
            })),
          ...generalCargoPickUpOrders.map(order => ({
            type: 'generalCargo' as const,
            referenceNumber: order.referenceNumber ?? undefined,
            commodityName: order.commodityName ?? undefined,
            commodityAttributes: order.commodityAttributes ?? undefined,
            plannedCargoAmount: order.plannedCargoAmount ?? undefined,
            location:
              generalCargoLocations.get(order.id)?.trim() ||
              t('noLocationProvided', 'No location provided'),
          })),
        ]

        const nonNumericOrders: RoutingSlipItem[] = await printoutsViewStore.mapNnrOrders(
          gateClerkViewStore.gateInViewStore.nnrOrders,
        )

        const pickUps = [...pickUpOrders, ...nonNumericOrders]

        if (dropOffs.length === 0 && pickUps.length === 0) {
          return
        }

        const document = (
          <RoutingSlip dropOffs={dropOffs} pickUps={pickUps} miniMap={viewStore.miniMap} t={t} />
        )

        const date = now.toLocaleDateString()
        await printoutsViewStore.generatePrintout('RoutingSlip', document, date)
      },
      [
        gateClerkViewStore.gateInViewStore.nnrOrders,
        gateClerkViewStore.gateInViewStore.dropOffOrders,
        gateClerkViewStore.gateInViewStore.pickUpOrders,
        gateInData,
        printoutsViewStore,
        viewStore.routingInfoData,
        t,
      ],
    )

    const generateEirPdfForOrders = useCallback(
      async (
        now: Date,
        orders: IInspectContainerFormData[],
        truckPlate: string,
        truckCompanyName?: string,
        driverName?: string,
        driverID?: string,
      ) => {
        const master = masterHoldTypes.get()
        for (const order of orders.filter(o => {
          const holds =
            o.journeyId != null
              ? (holdItemStore.holdsByContainerJourneyId[o.journeyId] ?? o.holds ?? [])
              : (o.holds ?? [])
          return canOrderProceed({
            allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
            direction: o.direction,
            hasBlockingHold: getHoldEnforcementResult(
              holds,
              o.direction === CarrierVisitDirection.Inbound ? 'GateIn' : 'GateOut',
              o.direction,
              master,
            ).isBlocking,
            isOnTerminal: o.isOnTerminal,
            skipReleaseOrder: tenantStore.skipReleaseOrder,
          })
        })) {
          const eirData = await generateEirDataForOrder(order, now, {
            truckPlate,
            truckCompanyName,
            driverName,
            driverID,
            fetchLinkedOrderData,
            orderItemStore,
            vesselVisitItemStore,
          })

          const document = (
            <EquipmentInterchangeReceipt
              key={`eir-${eirData.containerNumber}`}
              data={eirData}
              t={t}
            />
          )

          const date = now.toLocaleDateString()
          await printoutsViewStore.generatePrintout(
            'Eir',
            document,
            date,
            order.containerNumber,
            order.id,
          )
        }
      },
      [printoutsViewStore, t],
    )

    const generateEirPdf = useCallback(
      async (now: Date) => {
        if (direction === CarrierVisitDirection.Inbound && gateInData) {
          await generateEirPdfForOrders(
            now,
            gateInData.dropOffOrders.filter(o => !o.commodityId),
            gateInData.truckPlate,
            gateInData.truckCompanyName,
            gateInData.driverName,
            gateInData.driverID,
          )
        }

        if (direction === CarrierVisitDirection.Outbound && gateOutData) {
          await generateEirPdfForOrders(
            now,
            gateOutData.checkOrders.filter(o => o.direction === CarrierVisitDirection.Outbound),
            gateOutData.truckPlate,
            gateOutData.truckCompanyName,
            gateOutData.driverName,
            gateOutData.driverID,
          )
        }
      },
      [direction, gateInData, gateOutData, generateEirPdfForOrders],
    )

    const generateDocuments = useCallback(async () => {
      viewStore.setLoading(true)
      const now = new Date()

      try {
        if (direction === CarrierVisitDirection.Inbound) {
          await generateRoutingSlipPdf(now)
        }
        await generateEirPdf(now)

        await printoutsViewStore.uploadDocuments()
      } catch (error) {
        console.error('Error generating the document', error)
      } finally {
        viewStore.setLoading(false)
      }
    }, [viewStore, direction, printoutsViewStore, generateEirPdf, generateRoutingSlipPdf])

    useEffect(() => {
      generateDocuments()
    }, [generateDocuments])

    const handleDocumentClick = (url: string) => {
      window.open(url, '_blank')
    }

    const handleDoneClick = () => {
      gateClerkViewStore.gateInViewStore.reset()
      viewStore.reset()
      navigationStore.goTo(1)
    }

    if (viewStore.isLoading) {
      return (
        <Loader
          show={true}
          helperText={
            direction === CarrierVisitDirection.Inbound
              ? t('checkingForRoutingInfoAndEir', 'Checking for routing info and EIR')
              : t('checkingForEir', 'Checking for EIR')
          }
        />
      )
    }

    return (
      <Box>
        <Header
          title={
            direction === CarrierVisitDirection.Inbound
              ? t('gateIn', 'Gate in')
              : t('gateOut', 'Gate out')
          }
          subTitle={gateLabel}
          rightRenderOption={
            <Button
              variant='contained'
              onClick={handleDoneClick}
              data-cy='gate-clerk-documents-page-btn'
            >
              {t('done', 'Done')}
            </Button>
          }
        />
        <StepperInfo title={t('documents', 'Documents')} />

        <Box sx={{ p: 2 }}>
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {printoutsViewStore.documents.map(document => (
              <ListItemButton
                key={document.name}
                data-cy={`gate-clerk-document-${document.name}`}
                onClick={() => handleDocumentClick(document.url)}
                sx={{
                  border: `1px solid ${theme.palette.grey[400]}`,
                  borderRadius: theme.customRadius.medium,
                  padding: 2,
                  alignItems: 'center',
                }}
              >
                <ListItemIcon>
                  <PictureAsPdfIcon color='error' fontSize='large' />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='subtitle1' sx={{ backgroundColor: '#fff' }}>
                      {document.name}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Box>
    )
  },
)

export default GateClerkDocumentsPage
